import {Page} from 'store/customer/entity/Page';
import {Configuration} from 'store/customer/entity/Configuration';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AppApi} from 'store/customer/api';
import {AppState, store} from 'store/customer/storeSetup';

interface RawConfiguration {
    DEV_SITE_NAME: string;
    GA_KEY?: string;
    GA_KEYS?: string;
    GTM_ID?: string;
    GTM_IDS?: string;
    RECAPTCHA_SITE_KEY?: string;
    BASE_URL?: string;
    POSTWOOD_UI_URL?: string;
}

export enum Type {
    PRODUCT_DIMENSION,
    PRODUCT_QUANTITY,
}

export interface Change {
    id: number;
    type: Type;
}

interface ApplicationInterface {
    page?: Page;
    reloadScript?: boolean;
    // NOTE: When updates are possibly unified, this can go away
    // Update 1. Message from manufacturers
    // Update 2. New Room assistant message
    updateMessageShown: boolean;
    showUpdateMessage: string;
    changes: Change[];

    pageRestricted?: boolean;
    restrictionMessageDismissed?: boolean;
}

const initialState: ApplicationInterface = {
    page: Page.PRODUCT,
    reloadScript: false,
    updateMessageShown: true,
    showUpdateMessage: '',
    changes: [],
    pageRestricted: false,
    restrictionMessageDismissed: false,
};

const applicationSlice = createSlice({
    name: 'application',
    initialState,
    reducers: {
        pageRestrictedSet: (state, {payload}: PayloadAction<boolean>) => {
            state.pageRestricted = payload;
        },
        restrictionMessageDismissedSet: (
            state,
            {payload}: PayloadAction<boolean>
        ) => {
            state.restrictionMessageDismissed = payload;
        },
        reloadScriptSet: (state, {payload}: PayloadAction<boolean>) => {
            state.reloadScript = payload;
        },
        pageSet: (state, {payload}: PayloadAction<Page>) => {
            state.page = payload;
        },
        updateMessageShownSet: (state, {payload}: PayloadAction<boolean>) => {
            state.updateMessageShown = payload;
        },
        showUpdateMessageSet: (state, {payload}: PayloadAction<string>) => {
            state.showUpdateMessage = payload;
        },
        addChange: (state, {payload}: PayloadAction<Change>) => {
            const existing = state.changes.findIndex(
                (change) =>
                    change.type == payload.type && change.id == payload.id
            );

            if (existing == -1) {
                state.changes.push(payload);
            }
        },
        removeChange: (state, {payload}: PayloadAction<Change>) => {
            state.changes = state.changes.filter((item) => {
                return !(item.id == payload.id && item.type == payload.type);
            });
        },
        clearChanges: (state) => {
            state.changes = [];
        },
        clearChangesByType: (state, {payload}: PayloadAction<Type>) => {
            state.changes = state.changes.filter(
                (item) => item.type !== payload
            );
        },
    },
});

export const selectReloadScript = (store: AppState) =>
    store.application.reloadScript;
export const selectPage = (store: AppState) => store.application.page;
export const selectUpdateMessageShown = (store: AppState) =>
    store.application.updateMessageShown;
export const selectShowUpdateMessage = (store: AppState) => {
    return store.application.showUpdateMessage;
};
export const selectChanges = (store: AppState) => store.application.changes;
export const selectChangesByType = (store: AppState, type: Type): Change[] => {
    return store.application.changes.filter((c) => c.type == type);
};
export const selectPageRestricted = (store: AppState) =>
    store.application.pageRestricted;
export const selectRestrictionMessageDismissed = (store: AppState) =>
    store.application.restrictionMessageDismissed;

export const {
    pageSet,
    reloadScriptSet,
    updateMessageShownSet,
    showUpdateMessageSet,
    addChange,
    removeChange,
    clearChanges,
    clearChangesByType,
    pageRestrictedSet,
    restrictionMessageDismissedSet,
} = applicationSlice.actions;

// created this to call it easily with out having to use dispatch function every time
export const dispatchClearChanges = () => {
    store.dispatch(clearChanges());
};

export default applicationSlice.reducer;

interface KeySet {
    key:
        | Configuration['googleAnalyticsKey']
        | Configuration['googleTagManagerId'];
    keys:
        | Configuration['googleAnalyticsKeys']
        | Configuration['googleTagManagerIds'];
}

const isKeyList = (k: unknown): k is Record<string, string> => {
    return typeof k == 'object';
};

const getGaKeys = (
    keyInput: string | null,
    keysInput: string | null
): KeySet => {
    let key = null;
    let keys = null;

    const parsedKeys: unknown = JSON.parse(keysInput);

    if (typeof parsedKeys == 'string') {
        key = keysInput;
        keys = null;
    } else {
        key = typeof keyInput == 'string' && keyInput != '' ? keyInput : null;

        if (isKeyList(parsedKeys)) {
            keys = parsedKeys;
        }
    }

    return {key, keys};
};

const ConfigurationApi = AppApi.injectEndpoints({
    endpoints: (builder) => ({
        getConfiguration: builder.query<Configuration, void>({
            query: () => '../configuration/',
            transformResponse: (response: RawConfiguration) => {
                const {key: gaKey, keys: gaKeys} = getGaKeys(
                    response.hasOwnProperty('GA_KEY') ? response.GA_KEY : null,
                    response.hasOwnProperty('GA_KEYS') ? response.GA_KEYS : null
                );
                const {key: gtmId, keys: gtmIds} = getGaKeys(
                    response.hasOwnProperty('GTM_ID') ? response.GTM_ID : null,
                    response.hasOwnProperty('GTM_IDS') ? response.GTM_IDS : null
                );

                return {
                    environmentName: response.DEV_SITE_NAME,
                    googleAnalyticsKey: gaKey,
                    googleAnalyticsKeys: gaKeys,
                    googleTagManagerId: gtmId,
                    googleTagManagerIds: gtmIds,
                    googleRecaptchaKey: response?.RECAPTCHA_SITE_KEY,
                    baseUrl: response?.BASE_URL,
                    postwoodUiUrl: response?.POSTWOOD_UI_URL,
                };
            },
            providesTags: ['Configuration'],
        }),
    }),
});

export const {useGetConfigurationQuery} = ConfigurationApi;
